import { useMutation, useQuery } from "@tanstack/react-query";
import { TeamApi } from "../api/team-api";
import {
  CreateTeamDto,
  TeamDto,
  TeamMembersDto,
  UpdateTeamDto,
  UpdateTeamMemberDto,
} from "@repo/api/team";
import Toast from "../components/Toast";
import { queryClient } from "./query-client";
import { userQueryKeys } from "./user-queries";

export function useCreateTeamMutation({
  onSuccess,
  onError,
}: {
  onSuccess?: (data: TeamDto) => void;
  onError?: (error: Error) => void;
}) {
  return useMutation({
    mutationKey: teamQueryKeys.createTeam(),
    mutationFn: (data: CreateTeamDto) => TeamApi.createTeam(data),
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({
        queryKey: userQueryKeys.all(),
        type: "all",
      });
      onSuccess?.(data);
    },
    onError: (error) => {
      Toast.error("Failed to create team");
      onError?.(error);
    },
  });
}

export function useUpdateTeamMutation({
  teamExternalId,
  onSuccess,
  onError,
}: {
  teamExternalId: string;
  onSuccess?: (data: TeamDto) => void;
  onError?: (error: Error) => void;
}) {
  return useMutation({
    mutationKey: teamQueryKeys.updateTeam(teamExternalId),
    mutationFn: (data: UpdateTeamDto) =>
      TeamApi.updateTeam(data, teamExternalId),
    onSuccess,
    onError,
  });
}

export function useTeamQuery(teamExternalId: string) {
  return useQuery({
    queryKey: teamQueryKeys.getTeam(teamExternalId),
    queryFn: () => TeamApi.getTeam(teamExternalId),
    staleTime: Infinity,
    meta: {
      errorMessage: "Failed to fetch team",
    },
  });
}

export function useTeamMembersQuery(teamExternalId: string) {
  return useQuery({
    queryKey: teamQueryKeys.getTeamMembers(teamExternalId),
    queryFn: () => TeamApi.getTeamMembers(teamExternalId),
    staleTime: Infinity,
    meta: {
      errorMessage: "Failed to fetch team members",
    },
  });
}

export function useUpdateTeamMemberMutation({
  teamExternalId,
  onSuccess,
  onError,
  onMutate,
}: {
  teamExternalId: string;
  onSuccess?: (data: TeamMembersDto) => void;
  onError?: (
    error: Error,
    variables: {
      teamMemberExternalId: string;
      data: UpdateTeamMemberDto;
    },
    context: any,
  ) => void;
  onMutate?: (variables: {
    teamMemberExternalId: string;
    data: UpdateTeamMemberDto;
  }) => Promise<any> | any;
}) {
  return useMutation({
    mutationKey: teamQueryKeys.updateTeamMember(teamExternalId),
    mutationFn: ({
      teamMemberExternalId,
      data,
    }: {
      teamMemberExternalId: string;
      data: UpdateTeamMemberDto;
    }) => TeamApi.updateTeamMember(teamExternalId, teamMemberExternalId, data),
    onSuccess,
    onMutate,
    onError,
  });
}

export function useDeleteTeamMemberMutation({
  teamExternalId,
  onSuccess,
  onError,
}: {
  teamExternalId: string;
  onSuccess?: (data: TeamMembersDto) => void;
  onError?: (error: Error) => void;
}) {
  return useMutation({
    mutationKey: teamQueryKeys.deleteTeamMember(teamExternalId),
    mutationFn: (teamMemberExternalId: string) =>
      TeamApi.deleteTeamMember(teamExternalId, teamMemberExternalId),
    onSuccess,
    onError,
  });
}

export const teamQueryKeys = {
  createTeam: () => ["team", "create"],
  updateTeam: (teamExternalId: string) => [
    "team",
    "update",
    { teamExternalId },
  ],
  getTeam: (teamExternalId: string) => ["team", { teamExternalId }],
  getTeamMembers: (teamExternalId: string) => [
    "team",
    "members",
    { teamExternalId },
  ],
  deleteTeamMember: (teamExternalId: string) => [
    "team",
    "members",
    "deleteMember",
    { teamExternalId },
  ],
  updateTeamMember: (teamExternalId: string) => [
    "team",
    "members",
    "updateMember",
    { teamExternalId },
  ],
};
